'use client';

import {useState, useEffect} from 'react';
import {useSearchParams} from 'next/navigation';
import {useLocale, useTranslations} from 'next-intl';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';

const amounts = [10, 20, 50, 100, 200, 500];

interface ProjetInfo {
  id: number;
  titre_fr: string;
  categorie: string;
  budget_objectif: number;
  montant_collecte: number;
  gouvernorat_id?: number;
}

export default function DonForm() {
  const locale = useLocale();
  const t = useTranslations('don');
  const searchParams = useSearchParams();
  const projetId = searchParams.get('projet_id');

  const [projet, setProjet] = useState<ProjetInfo | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [connectedUser, setConnectedUser] = useState<{nom: string; email: string} | null>(null);
  const [devise, setDevise] = useState('TND');

  useEffect(() => {
    const stored = localStorage.getItem('utss_user');
    if (stored) {
      const u = JSON.parse(stored);
      if (u.email) setConnectedUser({nom: u.nom, email: u.email});
    }
  }, []);
  const [submitted, setSubmitted] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [gateway, setGateway] = useState('konnect');
  const [anonyme, setAnonyme] = useState(false);
  const [recurrent, setRecurrent] = useState(false);
  const [message, setMessage] = useState('');

  const currentAmount = selectedAmount || (customAmount ? Number(customAmount) : 0);

  useEffect(() => {
    if (!projetId) return;
    fetch(`/api/v1/projets/${projetId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProjet(data); })
      .catch(() => {});
  }, [projetId]);

  function handleAmountClick(amount: number) {
    setSelectedAmount(amount);
    setCustomAmount('');
    setAmountError('');
  }

  function handleCustomChange(val: string) {
    setCustomAmount(val);
    setSelectedAmount(null);
    const num = Number(val);
    if (val && (isNaN(num) || num < 5)) {
      setAmountError('Le montant minimum est de 5 TND');
    } else if (num > 50000) {
      setAmountError('Le montant maximum est de 50 000 TND');
    } else {
      setAmountError('');
    }
  }

  async function handleSubmit() {
    if (currentAmount < 5) {
      setAmountError('Veuillez selectionner ou saisir un montant (min. 5 TND)');
      return;
    }
    // Get donateur info from localStorage if connected
    const stored = localStorage.getItem('utss_user');
    const user = stored ? JSON.parse(stored) : null;

    const body = {
      montant: currentAmount,
      projet_id: projetId ? Number(projetId) : 1,
      methode_paiement: gateway === 'konnect' ? 'konnect' : 'stripe',
      anonyme,
      recurrent,
      message: message || undefined,
      donateur_email: user?.email || undefined,
      donateur_nom: user?.nom || undefined,
    };

    try {
      const res = await fetch('/api/v1/dons/', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const err = await res.json().catch(() => ({}));
        setAmountError(err.detail || 'Erreur lors de l\'enregistrement du don');
      }
    } catch {
      setAmountError('Erreur de connexion au serveur');
    }
  }

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
          <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-primary mb-4">Merci pour votre générosité !</h1>
        <p className="text-muted text-lg mb-2">
          Votre don de <strong>{currentAmount} TND</strong> a ete enregistre
          {projet && <> pour le projet <strong>&laquo; {projet.titre_fr} &raquo;</strong></>}.
        </p>
        <p className="text-muted mb-8">Vous recevrez un reçu fiscal par email sous 48h.</p>
        <div className="flex justify-center gap-4">
          {projet && (
            <Button href={`/${locale}/projets/${projet.id}`} variant="primary" size="md">Voir le projet</Button>
          )}
          <Button href={`/${locale}/structures`} variant="outline" size="md">Explorer la carte</Button>
        </div>
      </div>
    );
  }

  const pct = projet && projet.budget_objectif > 0
    ? Math.round((projet.montant_collecte / projet.budget_objectif) * 100) : null;
  const restant = projet ? Math.max(0, projet.budget_objectif - projet.montant_collecte) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-8">
        <div className="mb-4">
          <a
            href={`/${locale}/structures`}
            style={{color: '#1B5E20', textDecorationColor: 'transparent'}}
            className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            {t('back_to_map')}
          </a>
        </div>
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src="/images/utss/rose.svg" alt="" className="w-8 h-8 opacity-60" />
          <h1 className="text-3xl font-bold text-secondary">{t('title')}</h1>
        </div>
        <p className="text-muted">
          Votre don est deductible d&apos;impots (Article 38 LF2026). Recevez votre recu fiscal automatiquement.
        </p>
      </div>

      {/* Projet info card */}
      {projet && (
        <Card className="mb-6 !p-0 overflow-hidden">
          <div className="bg-primary/5 border-s-4 border-primary p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-primary font-medium uppercase tracking-wide mb-1">{t('for_project')}</p>
                <h2 className="text-lg font-bold text-foreground">{projet.titre_fr}</h2>
                <p className="text-sm text-muted mt-1 capitalize">{projet.categorie}</p>
              </div>
              <a href={`/${locale}/projets/${projet.id}`} className="text-xs text-primary hover:underline shrink-0 ms-3">Voir →</a>
            </div>
            {pct !== null && restant !== null && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted">{projet.montant_collecte.toLocaleString()} TND collectes</span>
                  <span className="font-bold text-primary">{pct}%</span>
                </div>
                <div className="h-2 bg-border rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : 'bg-primary'}`} style={{width: `${Math.min(pct, 100)}%`}} />
                </div>
                <p className="text-xs text-muted mt-1">Reste a financer: <strong>{restant.toLocaleString()} TND</strong></p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Connexion status */}
      {connectedUser ? (
        <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          <span>{t('connected_as', {nom: connectedUser.nom})}</span>
        </div>
      ) : (
        <div className="mb-4 p-3 rounded-lg bg-surface border border-border flex items-center justify-between text-sm">
          <span className="text-muted">{t('login_prompt')}</span>
          <a href={`/${locale}/auth`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="font-medium hover:underline shrink-0 ml-2">{t('login')}</a>
        </div>
      )}

      <Card>
        <div className="mb-6">
          <h3 className="text-sm font-bold text-secondary mb-3">{t('amount')} ({devise})</h3>
          <div className="grid grid-cols-3 gap-3">
            {amounts.map(amount => (
              <button
                key={amount}
                onClick={() => handleAmountClick(amount)}
                className={`py-3 rounded-lg text-lg font-bold transition-all cursor-pointer border-2 ${
                  selectedAmount === amount
                    ? 'border-primary bg-primary/5 text-primary scale-[1.02]'
                    : 'border-border text-foreground hover:border-primary/30'
                }`}
              >
                {amount} TND
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label htmlFor="custom-amount" className="text-xs text-muted block mb-1">Autre montant (TND)</label>
            <input
              id="custom-amount"
              type="number"
              min={5}
              max={50000}
              inputMode="numeric"
              value={customAmount}
              onChange={e => handleCustomChange(e.target.value)}
              placeholder="Saisir un montant"
              className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
            />
            {amountError && <p className="text-xs text-red-600 mt-1">{amountError}</p>}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-bold text-secondary mb-3">{t('payment_method')}</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              {id: 'konnect', label: 'Konnect / Flouci', sub: 'Tunisie'},
              {id: 'stripe', label: 'Stripe / PayPal', sub: 'International'},
            ].map(gw => (
              <button
                key={gw.id}
                onClick={() => setGateway(gw.id)}
                className={`py-3 px-4 rounded-lg border-2 transition-all cursor-pointer text-start ${
                  gateway === gw.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                }`}
              >
                <span className="text-sm font-medium block">{gw.label}</span>
                <span className="text-xs text-muted">{gw.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Devise */}
        {gateway === 'stripe' && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-secondary mb-2">{t('currency')}</h3>
            <div className="flex gap-2">
              {['TND', 'EUR', 'USD', 'GBP', 'CAD'].map(d => (
                <button key={d} onClick={() => setDevise(d)} className={`px-3 py-1.5 text-xs rounded-lg border cursor-pointer transition-colors ${devise === d ? 'border-primary bg-primary/5 font-bold text-primary' : 'border-border text-muted'}`}>{d}</button>
              ))}
            </div>
          </div>
        )}

        {/* Options */}
        <div className="mb-6 space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={anonyme} onChange={e => setAnonyme(e.target.checked)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary" />
            Don anonyme (votre nom ne sera pas affiche)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={recurrent} onChange={e => setRecurrent(e.target.checked)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary" />
            Don recurrent (mensuel)
          </label>
          <div>
            <label htmlFor="don-message" className="text-xs text-muted block mb-1">Message / dedicace (optionnel)</label>
            <input id="don-message" type="text" value={message} onChange={e => setMessage(e.target.value)} maxLength={500} placeholder="Un mot d'encouragement..." className="w-full px-3 py-2 rounded-lg border border-border bg-white text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none" />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={currentAmount < 5}
          className="w-full py-3.5 rounded-lg bg-secondary text-white font-bold text-lg hover:bg-secondary-light transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentAmount > 0 ? `${t('confirm')} — ${currentAmount} TND` : t('confirm')}
        </button>

        {/* Reassurance badges — CDC 7.1 */}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="flex flex-col items-center gap-1 text-[10px] text-muted">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            {t('secure_payment')}
          </div>
          <div className="flex flex-col items-center gap-1 text-[10px] text-muted">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {t('tax_receipt')}
          </div>
          <div className="flex flex-col items-center gap-1 text-[10px] text-muted">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            {t('full_to_project')}
          </div>
        </div>
      </Card>

      <Card className="mt-6">
        <h3 className="text-xl font-bold text-center text-secondary mb-4">{t('where_goes')}</h3>
        {projet ? (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            </div>
            <p className="text-base text-foreground font-medium mb-2">
              <strong>100%</strong> de votre don finance directement le projet :
            </p>
            <p className="text-lg font-bold text-primary mb-3">&laquo; {projet.titre_fr} &raquo;</p>
            <p className="text-sm text-muted capitalize mb-1">{projet.categorie}</p>
            <p className="text-xs text-muted">
              L&apos;UTSS garantit que l&apos;integralite de votre contribution est affectee au projet que vous avez choisi.
              Les frais de fonctionnement sont couverts par les subventions publiques.
            </p>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-muted mb-4">
              Votre don est affecte aux projets sociaux de l&apos;UTSS selon les besoins prioritaires sur le terrain.
            </p>
            <a href={`/${locale}/structures`} style={{color: '#1B5E20'}} className="text-sm font-medium hover:underline">
              Choisir un projet specifique sur la carte →
            </a>
          </div>
        )}
      </Card>
    </div>
  );
}
